import { createHttpClient, type CreateHttpClientOptions } from './http';
import { serviceUrls } from '../config/serviceUrls';

const FALLBACK_TUTOR =
  import.meta.env.VITE_AI_TUTOR_FALLBACK_URL || 'http://localhost:8007';

/**
 * AI Tutor mounts `/admin/prompt-library` and `/api/...` at the **root** of the service.
 * A base like `http://localhost:8007/api` would produce `/api/admin/...` → **404**.
 */
export function normalizeTutorBaseUrl(raw: string): string {
  let u = raw.trim().replace(/\/+$/, '');
  if (u.endsWith('/api')) {
    u = u.slice(0, -4);
  }
  return u;
}

/** Used for ai-tutor admin + public editor APIs that may fall back between gateway and direct service */
export const TUTOR_HTTP_TIMEOUT_MS = 30_000;

/**
 * Base URLs to try for ai-tutor HTTP calls (first success wins).
 *
 * - If `VITE_AI_TUTOR_BASE_URL` is set, it is tried first, then fallback + gateway.
 * - In **dev** without that env, **direct tutor** (`:8007`) is tried before the gateway so a
 *   missing or slow gateway does not block the UI for minutes (axios has no default timeout).
 * - In **prod**, gateway is preferred; direct URL is fallback.
 */
export function getTutorHttpBaseCandidates(): string[] {
  const gateway = normalizeTutorBaseUrl(serviceUrls.gateway);
  const fallback = normalizeTutorBaseUrl(FALLBACK_TUTOR);
  const explicit = import.meta.env.VITE_AI_TUTOR_BASE_URL?.trim();

  if (explicit) {
    return Array.from(new Set([normalizeTutorBaseUrl(explicit), fallback, gateway]));
  }
  if (import.meta.env.DEV) {
    return [fallback, gateway];
  }
  return [gateway, fallback];
}

export function createTutorHttpClients(options?: CreateHttpClientOptions): ReturnType<typeof createHttpClient>[] {
  return getTutorHttpBaseCandidates().map((base) =>
    createHttpClient(base, { ...options, timeoutMs: options?.timeoutMs ?? TUTOR_HTTP_TIMEOUT_MS })
  );
}
